import type { FC, ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import type { ILocalAudioTrack, IRemoteAudioTrack } from 'agora-rtc-sdk-ng'
import type { CSSRules, Directive } from 'twind/css'
import { css } from 'twind/css'

import { Image } from '@core/components/Image'
import { AUDIO_VISUALIZER_TEXT_WRAPPER } from '@core/components/testIds'
import {
  audioVisualizerText,
  audioVisualizerTextWrapper,
} from '@core/components/twind/AudioVisualizer/AudioVisualizer.twind'
import { useTwindContext } from '@core/twind'

interface Props {
  children?: ReactNode
  className?: string
  text?: string
  src?: string
  audioTrack?: IRemoteAudioTrack | ILocalAudioTrack
  mediaStream?: MediaStream
  size?: number
  textWrapperCss?: Directive<CSSRules>
  expansionFactor?: number
}

/**
 * This component is a visualizer that hooks to an agora audio track and pulse
 */
export const AudioVisualizer: FC<Props> = ({
  children,
  text,
  src,
  audioTrack,
  mediaStream,
  size = 124,
  textWrapperCss,
  expansionFactor = 2,
  ...props
}) => {
  const { tw } = useTwindContext()

  const [showDefaultAnimation, setShowDefaultAnimation] = useState(false)

  const pulsingEffectRef = useRef<HTMLDivElement>(null)

  const findClosestTransformScale = useCallback(
    (number: number) => {
      const lowerBound = 1
      const upperBound = 1.4
      const increment = 0.075

      const totalSteps = (upperBound - lowerBound) / increment
      const amplifiedPercent = Math.min(1, number * expansionFactor)
      const steps = Math.round(totalSteps * amplifiedPercent)
      return lowerBound + steps * increment
    },
    [expansionFactor],
  )

  const addAudioPulsingEffect = useCallback(() => {
    if (audioTrack || mediaStream) {
      try {
        const audioContext = new AudioContext()
        const analyzerNode = audioContext.createAnalyser()
        if (audioTrack) {
          const mediaStream = new MediaStream()
          mediaStream.addTrack(audioTrack?.getMediaStreamTrack())
          const sourceNode = audioContext.createMediaStreamSource(mediaStream)
          sourceNode.connect(analyzerNode)
        } else if (mediaStream) {
          const sourceNode = audioContext.createMediaStreamSource(mediaStream)
          sourceNode.connect(analyzerNode)
        }

        const processAudio = () => {
          // Number of samples
          const bufferLength = analyzerNode.fftSize
          const dataArray = new Float32Array(bufferLength)
          // Copies the current frequency data into a Float32Array array passed into it
          analyzerNode.getFloatTimeDomainData(dataArray)

          let total = 0
          // Total is the sum of the absolute values of the samples
          for (let i = 0; i < bufferLength; i++) {
            total += Math.abs(dataArray[i])
          }
          // rms (root mean square) is used to determine the average power level of the audio signal
          const rms = Math.sqrt(total / bufferLength)
          if (pulsingEffectRef.current) {
            if (rms > 0.1) {
              const scale = `scale(${findClosestTransformScale(rms)})`
              pulsingEffectRef.current.style.transform = scale
            } else {
              pulsingEffectRef.current.style.transform = `scale(1)`
            }
          }
        }

        let animationFrameId = requestAnimationFrame(function update() {
          processAudio()
          animationFrameId = requestAnimationFrame(update)
        })

        return () => {
          cancelAnimationFrame(animationFrameId)
        }
      } catch (e) {
        // In case of error, we don't visualize the audio effect and falls back to default animation
        setShowDefaultAnimation(true)
      }
    }
  }, [audioTrack, findClosestTransformScale, mediaStream])

  useEffect(() => {
    if (audioTrack || mediaStream) {
      const cleanup = addAudioPulsingEffect()
      return cleanup
    }
  }, [addAudioPulsingEffect, audioTrack, mediaStream])

  const audioVisualizerTextWrapperStyle = css({
    width: `${size}px`,
    height: `${size}px`,
  })

  const audioVisualizerTextWrapperAnimationStyle = showDefaultAnimation
    ? css({
        animationName: 'pulse',
        animationDuration: '2s',
        animationIterationCount: 'infinite',
        animationTimingFunction: 'linear',
        '@keyframes pulse': {
          '0%': {
            transform: 'scale(1)',
            opacity: 0.7,
          },
          '50%': {
            transform: 'scale(1.2)',
            opacity: 1,
          },
          '100%': {
            transform: 'scale(1)',
            opacity: 0.7,
          },
        },
      })
    : css({
        transition: 'transform 0.2s steps(6, jump-none) 33.33ms',
      })

  return (
    <div className={tw?.('flex', 'justify-center', 'items-center')} {...props}>
      {text ? (
        <div className={tw?.(audioVisualizerText, 'font-semibold', 'absolute')}>
          {text.slice(0, 10)}
        </div>
      ) : null}
      {src ? (
        <Image
          className={tw?.(
            'absolute',
            'object-cover',
            'object-top',
            'z-10',
            'rounded-full',
          )}
          src={src}
          alt={text}
          width={`${size}px`}
          height={`${size}px`}
        />
      ) : null}
      <div
        ref={pulsingEffectRef}
        data-testid={AUDIO_VISUALIZER_TEXT_WRAPPER}
        className={tw?.(
          audioVisualizerTextWrapper,
          'rounded-full',
          audioVisualizerTextWrapperStyle,
          audioVisualizerTextWrapperAnimationStyle,
          textWrapperCss,
        )}
      >
        {children}
      </div>
    </div>
  )
}
